/**
 * The statutory notices the government footer links to.
 *
 * ## What these pages are, and are not
 *
 * SGDS requires a Privacy and a Terms of Use link on every page. It does not write the
 * notices — the Authority publishes those under its own corporate policy, and a contractor
 * cannot author them on its behalf.
 *
 * So each page states **only what is true of this system**, addressed to the officer
 * reading it, and says plainly that the Authority's published notice governs. Every
 * behaviour described was read out of this codebase rather than assumed. Where a fact would
 * have to be invented to round a paragraph off, the paragraph is not written — retention is
 * the clearest example and is deliberately absent: this system records a retention standard
 * against a record class and disposes of nothing, so any sentence about data being deleted
 * after a period would be false.
 *
 * ⚠ No clause number appears in this copy. An officer using this console has no idea what a
 * tender clause is, and a screen that cites one reads as a compliance matrix rendered as a
 * product.
 *
 * At deployment the Authority's published URLs replace these routes and the footer changes
 * with them.
 */

function Notice({ title, standfirst, children }: { title: string; standfirst: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">{title}</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">{standfirst}</p>
      </div>
      {children}
    </div>
  )
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="pt-card p-6 max-w-[80ch]">
      {/* h2 against the page's h1 — the notice is a flat document, so the heading
          level does not need to go deeper than one. */}
      <h2 className="text-lg font-semibold mb-2">{heading}</h2>
      <div className="flex flex-col gap-3 text-[var(--sgds-body-color-default)]">{children}</div>
    </section>
  )
}

export function PrivacyNoticePage() {
  return (
    <Notice
      title="Privacy Statement"
      standfirst="What this console records about your use of it, and who can see it."
    >
      <Section heading="PSD’s published Privacy Statement governs">
        <p>
          This is an internal system of the Public Service Division, and the Division’s
          corporate Privacy Statement under the Personal Data Protection Act applies to it in
          full. Where anything below appears to differ from that Statement, the Statement is
          the one that holds.
        </p>
      </Section>

      <Section heading="Your work on a bill is on the record">
        <p>
          Opening a bill, adjusting it, pausing it, approving it and exporting a report are
          recorded in the audit trail against your own identity and the time. You can read
          that trail yourself on the Audit screen: it is not a log kept away from you, and it
          records your own actions as well as everybody else’s.
        </p>
        <p>
          The trail is what makes a pro-ration decision answerable months later, which is the
          same reason it cannot be edited to tidy up a mistake. A correction is another entry,
          not a rewritten one.
        </p>
      </Section>

      <Section heading="What you can see is scoped to your role">
        <p>
          Your role decides which screens you can open and which records you can act on.
          Where a screen shows you less than a colleague sees on the same screen, that is the
          scope working rather than a fault, and where an action is not yours to take, the
          control is absent rather than disabled, so it cannot look momentarily unavailable.
        </p>
      </Section>

      <Section heading="Every figure traces back to what produced it">
        <p>
          A pro-rated amount is computed from the ward rules, rates and dates held in this
          system, against the service date rather than the submission date. The provenance of
          each figure, which rule version and which rate applied, is shown with it, so a
          number you are asked to justify can be traced rather than defended from memory.
        </p>
      </Section>
    </Notice>
  )
}

export function TermsNoticePage() {
  return (
    <Notice title="Terms of Use" standfirst="The terms on which you may use this console.">
      <Section heading="PSD’s published Terms of Use govern">
        <p>
          This system is provided by the Public Service Division and its published Terms of
          Use apply. The terms below describe how those obligations land on your day-to-day
          use; they neither add to nor reduce them.
        </p>
      </Section>

      <Section heading="Your account is yours alone">
        <p>
          Access is granted to you personally, for the duties of your appointment. Do not use
          another officer’s account and do not let anyone use yours: the audit trail
          attributes every action to the signed-in officer, so work done under your account is
          accounted to you.
        </p>
      </Section>

      <Section heading="An export keeps the sensitivity of the record behind it">
        <p>
          A report or a downloaded file inherits the sensitivity of the bills and personal
          details it was built from. Putting it into a spreadsheet, an email or a slide does
          not change that, and the handling rules that applied on screen continue to apply to
          the copy you took.
        </p>
      </Section>

      <Section heading="A computed figure supports your decision; it is not the decision">
        <p>
          This console computes pro-ration from the rules and dates it holds. Read the
          provenance shown beside a figure before you act on it, if the rule version or the
          rate is not the one you expected, the figure is answering a different question from
          the one you are asking. Raise it rather than working around it.
        </p>
      </Section>
    </Notice>
  )
}

export function ContactNoticePage() {
  return (
    <Notice
      title="Contact and feedback"
      standfirst="Where to take a question about a record, and where to report a problem with the console."
    >
      <Section heading="A question about a bill or about your access">
        <p>
          Your system administrator holds the roles and the reference data. Take to them
          anything about access you believe you should have and do not, and anything about a
          ward class, rate or rule that looks wrong: they can see and change the reference
          data that produced the figure, and you cannot.
        </p>
      </Section>

      <Section heading="Feedback on this console">
        <p id="feedback">
          Tell your system administrator what slowed you down or read wrongly: a column you
          always have to widen, a figure you had to check twice, a word that means something
          different in your team. A specific example is worth more than a general impression,
          because it can be reproduced.
        </p>
      </Section>

      <Section heading="A fault, or a security weakness">
        <p>
          Report a page that will not load, a control that does nothing, or a figure you
          believe is wrong, to your system administrator. Include the bill reference and what
          you had just done: a fault that cannot be reproduced cannot be fixed.
        </p>
        <p>
          If you believe you have found a security weakness in this system, use the
          government’s Vulnerability Disclosure Programme, linked in the footer of every page,
          rather than describing the weakness in an email.
        </p>
      </Section>
    </Notice>
  )
}
